import {useState, useEffect, useRef} from 'react'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {MRT_API_BASEPATH} from '../ssr/constants'

export const SA_EMAIL_KEY = 'sa_sales_assistant_email'
export const SA_IMPERSONATING_KEY = 'sa_impersonating'
export const SA_TOKEN_KEY = 'sa_agent_token'
export const SA_SESSION_TOKEN_KEY = 'sa_session_token'

// How often the client re-validates an active agent session against the server.
// Combined with the short server-side preference cache, this bounds how long an
// agent can keep operating after the endlessAisleConfig kill-switch is flipped.
const SESSION_POLL_INTERVAL_MS = 60 * 1000

function readFromStorage(key) {
    try {
        return localStorage.getItem(key) || null
    } catch {
        return null
    }
}

function writeToStorage(key, value) {
    try {
        if (value) {
            localStorage.setItem(key, value)
        } else {
            localStorage.removeItem(key)
        }
    } catch {
        // localStorage may be unavailable in some environments
    }
}

// Drop every Sales Assistant marker from localStorage. Used when the session is
// definitively over (kill-switch tripped) so the banner wrapper unmounts and no
// further check-session calls are made.
function clearSessionStorage() {
    writeToStorage(SA_EMAIL_KEY, null)
    writeToStorage(SA_IMPERSONATING_KEY, null)
    writeToStorage(SA_TOKEN_KEY, null)
    writeToStorage(SA_SESSION_TOKEN_KEY, null)
}

export const useSalesAssistantSession = () => {
    const {getTokenWhenReady} = useAccessToken()
    const [isLoading, setIsLoading] = useState(true)
    const [isAuthenticated, setIsAuthenticated] = useState(() => !!readFromStorage(SA_EMAIL_KEY))
    // Initialise from localStorage so the values are immediately available
    // without waiting for the check-session network call.
    const [salesAssistantEmail, setSalesAssistantEmail] = useState(() =>
        readFromStorage(SA_EMAIL_KEY),
    )
    // Initialise from localStorage so the banner reflects impersonation state
    // immediately without waiting for the check-session network call.
    // SA_IMPERSONATING_KEY stores the customer email (or 'true' as fallback).
    const [salesAssistantCustomerEmail, setSalesAssistantCustomerEmail] = useState(() => {
        const val = readFromStorage(SA_IMPERSONATING_KEY)
        return val && val !== 'true' ? val : null
    })
    // Incremented by 'sa-session-refreshed' to re-run checkSession after
    // end-impersonation has set up the new token's SA markers.
    const [sessionCheckKey, setSessionCheckKey] = useState(0)
    // Whether the Sales Assistant (endless aisle) flow is enabled site-wide.
    // undefined while the first check-session is in flight; true/false afterwards.
    // Consumers gate the SA pages/banner on this without flashing during load.
    const [endlessAisleEnabled, setEndlessAisleEnabled] = useState(undefined)
    // Stable ref so the checkSession effect closure always calls the latest
    // getTokenWhenReady without adding it to the effect's dep array.
    const getTokenRef = useRef(getTokenWhenReady)
    getTokenRef.current = getTokenWhenReady
    // Same pattern for the current site id, sent to check-session so it can read
    // the site-scoped endlessAisleConfig preference.
    const {site, locale} = useMultiSite()
    const siteIdRef = useRef(site?.id)
    siteIdRef.current = site?.id
    // Current locale id (e.g. it-IT) sent to check-session so the server can read
    // the per-country enableEndlessAisle/endlessAisleConfig flag for this locale.
    const localeRef = useRef(locale?.id)
    localeRef.current = locale?.id

    // React to impersonation start/end without requiring a page reload.
    // The dashboard dispatches 'sa-impersonation-changed' after setting
    // SA_IMPERSONATING_KEY, and use-end-impersonation dispatches it after
    // removing it — so this effect keeps salesAssistantCustomerEmail in sync instantly.
    useEffect(() => {
        const onImpersonationChanged = () => {
            const val = readFromStorage(SA_IMPERSONATING_KEY)
            setSalesAssistantCustomerEmail(val && val !== 'true' ? val : null)
        }
        window.addEventListener('sa-impersonation-changed', onImpersonationChanged)
        return () => window.removeEventListener('sa-impersonation-changed', onImpersonationChanged)
    }, [])

    // Re-run checkSession when end-impersonation signals the new token is ready.
    useEffect(() => {
        const onSessionRefresh = () => setSessionCheckKey((k) => k + 1)
        window.addEventListener('sa-session-refreshed', onSessionRefresh)
        return () => window.removeEventListener('sa-session-refreshed', onSessionRefresh)
    }, [])

    // When any hook instance trips the kill-switch it dispatches 'sa-flow-disabled'
    // so every other consumer (dashboard, login page, banner) drops the session
    // state immediately rather than waiting for its own next poll.
    useEffect(() => {
        const onFlowDisabled = () => {
            setEndlessAisleEnabled(false)
            setIsAuthenticated(false)
            setSalesAssistantEmail(null)
            setSalesAssistantCustomerEmail(null)
        }
        window.addEventListener('sa-flow-disabled', onFlowDisabled)
        return () => window.removeEventListener('sa-flow-disabled', onFlowDisabled)
    }, [])

    useEffect(() => {
        let cancelled = false
        let intervalId

        const stopPolling = () => {
            if (intervalId) {
                clearInterval(intervalId)
                intervalId = undefined
            }
        }

        const checkSession = async () => {
            try {
                const token = await getTokenRef.current()
                const sessionToken = readFromStorage(SA_SESSION_TOKEN_KEY)
                const params = []
                if (siteIdRef.current)
                    params.push(`siteId=${encodeURIComponent(siteIdRef.current)}`)
                if (localeRef.current)
                    params.push(`locale=${encodeURIComponent(localeRef.current)}`)
                if (sessionToken)
                    params.push(`salesAssistantSessionToken=${encodeURIComponent(sessionToken)}`)
                const query = params.length ? `?${params.join('&')}` : ''
                const response = await fetch(
                    `${getAppOrigin()}${MRT_API_BASEPATH}/sales-assistant-check-session${query}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    },
                )
                const data = await response.json()
                if (cancelled) return

                // Kill-switch tripped: end the SA session server-side (cleans
                // InMemoryStorage markers) then tear down client state. Stop
                // polling first so no further checks queue while we await logout.
                if (data.endlessAisleEnabled === false) {
                    stopPolling()
                    // Best-effort: clear InMemoryStorage markers on the server so
                    // any subsequent check-session call by another tab also sees
                    // the session as inactive immediately. Only call logout when
                    // there is an active SA session to terminate — normal shoppers
                    // have no server-side markers to clear and the logout endpoint
                    // must not be invoked unnecessarily.
                    if (sessionToken) {
                        try {
                            await fetch(
                                `${getAppOrigin()}${MRT_API_BASEPATH}/sales-assistant-logout`,
                                {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        Authorization: `Bearer ${token}`,
                                    },
                                    body: JSON.stringify({
                                        salesAssistantSessionToken: sessionToken,
                                    }),
                                },
                            )
                        } catch {
                            // best-effort: failure must not block client-side teardown
                        }
                    }
                    if (cancelled) return
                    clearSessionStorage()
                    setEndlessAisleEnabled(false)
                    setIsAuthenticated(false)
                    setSalesAssistantEmail(null)
                    setSalesAssistantCustomerEmail(null)
                    // Banner wrapper re-checks its marker; sibling hook instances
                    // drop their session state in lock-step.
                    window.dispatchEvent(new CustomEvent('sa-session-ended'))
                    window.dispatchEvent(new CustomEvent('sa-impersonation-changed'))
                    window.dispatchEvent(new CustomEvent('sa-flow-disabled'))
                    return
                }

                setEndlessAisleEnabled(data.endlessAisleEnabled === true)
                if (data.active) {
                    setIsAuthenticated(true)
                    if (data.salesAssistantEmail) {
                        const hadMarker = !!readFromStorage(SA_EMAIL_KEY)
                        setSalesAssistantEmail(data.salesAssistantEmail)
                        writeToStorage(SA_EMAIL_KEY, data.salesAssistantEmail)
                        // The marker was absent (e.g. after a kill-switch cycle):
                        // notify the banner wrapper so it re-mounts immediately.
                        if (!hadMarker) {
                            window.dispatchEvent(new CustomEvent('sa-session-started'))
                        }
                    }
                    // salesAssistantCustomerEmail is managed exclusively via
                    // localStorage + sa-impersonation-changed events, so we
                    // do NOT override it here.
                } else {
                    // Definitive: no markers and the client holds no agent
                    // email — the session is genuinely gone.
                    setIsAuthenticated(false)
                    setSalesAssistantEmail(null)
                    writeToStorage(SA_EMAIL_KEY, null)
                    setSalesAssistantCustomerEmail(null)
                }
            } catch {
                // Transient network/token error: keep the current
                // optimistic state rather than logging the agent out on a blip.
            } finally {
                if (!cancelled) {
                    setIsLoading(false)
                }
            }
        }

        checkSession()
        // Re-validate on an interval only while an agent session marker is present,
        // so flipping the kill-switch logs the agent out within ~one interval even
        // on a page they are idling on. The check stops as soon as the flow is
        // disabled (above) or the marker disappears (e.g. after logout).
        // The interval is paused while the tab is hidden and fires immediately on
        // visibility restore, avoiding wasted requests from background tabs.
        intervalId = setInterval(() => {
            if (document.hidden) return
            if (readFromStorage(SA_EMAIL_KEY)) {
                checkSession()
            } else {
                stopPolling()
            }
        }, SESSION_POLL_INTERVAL_MS)

        const onVisibilityChange = () => {
            if (document.hidden) return
            // Tab became visible: run immediately if the marker is still present.
            if (readFromStorage(SA_EMAIL_KEY)) {
                checkSession()
            } else {
                stopPolling()
            }
        }
        document.addEventListener('visibilitychange', onVisibilityChange)

        return () => {
            cancelled = true
            stopPolling()
            document.removeEventListener('visibilitychange', onVisibilityChange)
        }
    }, [sessionCheckKey])

    return {
        isLoading,
        isAuthenticated,
        endlessAisleEnabled,
        salesAssistantEmail,
        salesAssistantCustomerEmail,
    }
}
